import { Row, Col, Form } from "react-bootstrap";
import PropTypes from 'prop-types';

const BasicInfoFields = ({
    title,
    setTitle,
    promotionType,
    setPromotionType,
    offerStatus,
    setOfferStatus,
    validFrom,
    setValidFrom,
    validTo,
    setValidTo,
    description,
    setDescription,
    imagePath,
    setImagePath,
    PromotionType,
    PromotionTypeLabels,
    OfferStatus,
    OfferStatusLabels
}) => {
    return (
        <>
            {/* Title and Promotion Type */}
            <Row className='mb-3'>
                <Col sm={6}>
                    <Form.Group className='form-group'>
                        <Form.Label>Title *</Form.Label>
                        <Form.Control
                            type='text'
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder='Enter content title'
                            required
                        />
                    </Form.Group>
                </Col>
                <Col sm={6}>
                    <Form.Group className='form-group'>
                        <Form.Label>Promotion Type *</Form.Label>
                        <Form.Select
                            value={promotionType}
                            onChange={(e) => setPromotionType(e.target.value)}
                            required
                        >
                            <option value="">Select promotion type</option>
                            <option value={PromotionType.DISCOUNT}>{PromotionTypeLabels[PromotionType.DISCOUNT]}</option>
                            <option value={PromotionType.BOGO}>{PromotionTypeLabels[PromotionType.BOGO]}</option>
                            <option value={PromotionType.COUPON}>{PromotionTypeLabels[PromotionType.COUPON]}</option>
                            <option value={PromotionType.BANNER}>{PromotionTypeLabels[PromotionType.BANNER]}</option>
                            <option value={PromotionType.SPOTLIGHT}>{PromotionTypeLabels[PromotionType.SPOTLIGHT]}</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {/* Active Toggle and Validity Dates */}
            <Row className='mb-3'>
                <Col sm={4}>
                    <Form.Group className='form-group'>
                        <Form.Label>Status *</Form.Label>
                        <Form.Select
                            value={offerStatus}
                            onChange={(e) => setOfferStatus(e.target.value)}
                            required
                        >
                            <option value={OfferStatus.ACTIVE}>{OfferStatusLabels[OfferStatus.ACTIVE]}</option>
                            <option value={OfferStatus.INACTIVE}>{OfferStatusLabels[OfferStatus.INACTIVE]}</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Manual control to enable/disable offer
                        </Form.Text>
                    </Form.Group>
                </Col>
                <Col sm={4}>
                    <Form.Group className='form-group'>
                        <Form.Label>Valid From</Form.Label>
                        <Form.Control
                            type='date'
                            value={validFrom}
                            onChange={(e) => setValidFrom(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            Start date (optional, effective immediately if empty)
                        </Form.Text>
                    </Form.Group>
                </Col>
                <Col sm={4}>
                    <Form.Group className='form-group'>
                        <Form.Label>Valid To</Form.Label>
                        <Form.Control
                            type='date'
                            value={validTo}
                            onChange={(e) => setValidTo(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            End date (optional, no expiry if empty)
                        </Form.Text>
                    </Form.Group>
                </Col>
            </Row>

            {/* Status indicator based on dates */}
            {(validFrom || validTo) && (
                <Row className='mb-3'>
                    <Col sm={12}>
                        <div
                            style={{
                                backgroundColor: '#f8f9fa',
                                border: '1px solid #dee2e6',
                                borderRadius: '6px',
                                padding: '10px 14px',
                                fontSize: '13px',
                                color: '#495057'
                            }}
                        >
                            <strong>📅 Effective Status:</strong>{' '}
                            {(() => {
                                const now = new Date();
                                now.setHours(0, 0, 0, 0);
                                const from = validFrom ? new Date(validFrom) : null;
                                const to = validTo ? new Date(validTo) : null;

                                if (offerStatus === OfferStatus.INACTIVE) {
                                    return <span style={{ color: '#6c757d', fontWeight: '600' }}>Inactive (manually disabled)</span>;
                                }

                                if (from && from > now) {
                                    return <span style={{ color: '#007bff', fontWeight: '600' }}>Scheduled (starts {from.toLocaleDateString()})</span>;
                                }

                                if (to && to < now) {
                                    return <span style={{ color: '#dc3545', fontWeight: '600' }}>Expired (ended {to.toLocaleDateString()})</span>;
                                }

                                return <span style={{ color: '#28a745', fontWeight: '600' }}>Active now</span>;
                            })()}
                        </div>
                    </Col>
                </Row>
            )}

            {/* Description */}
            <Row className='mb-3'>
                <Col sm={12}>
                    <Form.Group className='form-group'>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as='textarea'
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder='Enter banner description'
                        />
                    </Form.Group>
                </Col>
            </Row>

            {/* Image Path */}
            <Row className='mb-3'>
                <Col sm={12}>
                    <Form.Group className='form-group'>
                        <Form.Label>
                            Image Path/URL {(promotionType === PromotionType.SPOTLIGHT || promotionType === PromotionType.BANNER) && '*'}
                        </Form.Label>
                        <Form.Control
                            type='text'
                            value={imagePath}
                            onChange={(e) => setImagePath(e.target.value)}
                            placeholder='Enter image path or URL'
                            required={promotionType === PromotionType.SPOTLIGHT || promotionType === PromotionType.BANNER}
                        />
                    </Form.Group>
                </Col>
            </Row>
        </>
    );
};

BasicInfoFields.propTypes = {
    title: PropTypes.string.isRequired,
    setTitle: PropTypes.func.isRequired,
    promotionType: PropTypes.string.isRequired,
    setPromotionType: PropTypes.func.isRequired,
    offerStatus: PropTypes.string.isRequired,
    setOfferStatus: PropTypes.func.isRequired,
    validFrom: PropTypes.string.isRequired,
    setValidFrom: PropTypes.func.isRequired,
    validTo: PropTypes.string.isRequired,
    setValidTo: PropTypes.func.isRequired,
    description: PropTypes.string.isRequired,
    setDescription: PropTypes.func.isRequired,
    imagePath: PropTypes.string.isRequired,
    setImagePath: PropTypes.func.isRequired,
    PromotionType: PropTypes.object.isRequired,
    PromotionTypeLabels: PropTypes.object.isRequired,
    OfferStatus: PropTypes.object.isRequired,
    OfferStatusLabels: PropTypes.object.isRequired
};

export default BasicInfoFields;

